import React, { useState, useMemo } from 'react';
import { Card, Text, Group, Table, Pagination } from '@mantine/core';
import { TableProps } from '../../types/metrics';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/apiClient';
import './TabbedTableRenderer.css';
import '../ui/MetricTable.css';
import { BmBadge, isCydRateColumn } from '../ui/BmBadge';
import { MetricTitle } from '../ui/MetricTitle';
import { MetricTableToolbar, PillTabOption } from '../ui/MetricTableToolbar';
import { sortCydTableRowsByRateDesc } from '../../utils/cydTableRow';
import { getCydRateGrayBadgeStyle, getCydRateRange } from '../../utils/cydRateBadgeStyle';
import { formatTableHeaderLabel, getTableColumnClass } from '../../utils/tableColumnAlign';

const TAB_SEARCH_PLACEHOLDERS: Record<string, string> = {
  products: 'Buscar productos...',
  variants: 'Buscar variantes...',
  customers: 'Buscar clientes...',
  'new-customers': 'Buscar clientes nuevos...',
  'recurring-customers': 'Buscar clientes recurrentes...',
  period: 'Buscar clientes del período...',
  historical: 'Buscar clientes históricos...',
  new: 'Buscar clientes nuevos...',
  recurring: 'Buscar clientes recurrentes...',
};

const TAB_ENTITY_LABELS: Record<string, string> = {
  products: 'productos',
  variants: 'variantes',
  customers: 'clientes',
  'new-customers': 'clientes nuevos',
  'recurring-customers': 'clientes recurrentes',
  period: 'clientes del período',
  historical: 'clientes históricos',
  new: 'clientes nuevos',
  recurring: 'clientes recurrentes',
};

const TabbedTableRenderer: React.FC<TableProps> = ({ 
  config, 
  data, 
  loading, 
  error,
  pageSize = 10,
  storeId,
  startDate,
  endDate
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState<string>('products');
  const [initialTabSet, setInitialTabSet] = useState(false);
  const [minUnitsSold, setMinUnitsSold] = useState(5);
  const isTopCydProducts = config.id === 'top-cyd-products';

  React.useEffect(() => {
    if (config.id !== 'top-cyd-products') {
      setDebouncedSearch('');
      return;
    }
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm, config.id]);

  // Función para obtener el endpoint específico según la pestaña activa
  const getSpecificEndpoint = (tab: string) => {
    if (!storeId) return null;
    
    const baseUrl = '/api/metrics';
    
    // Si la configuración tiene pestañas personalizadas, usarlas
    if (config.tabs && config.tabs.length > 0) {
      const tabConfig = config.tabs.find(t => t.id === tab);
      if (tabConfig) {
        return tabConfig.apiEndpoint.replace('{storeId}', storeId);
      }
    }
    
    // Lógica original para compatibilidad
    switch (tab) {
      case 'customers':
        return `${baseUrl}/${storeId}/customers/top`;
      case 'new-customers':
        return `${baseUrl}/${storeId}/customers/top-new`;
      case 'recurring-customers':
        return `${baseUrl}/${storeId}/customers/top-recurring`;
      case 'products':
        if (config.id === 'top-cyd-products') {
          return `${baseUrl}/${storeId}/cyd/products-table`;
        }
        return null;
      case 'variants':
        // Determinar si es CyD o productos normales basado en el título
        if (config.title?.toLowerCase().includes('cyd')) {
          return `${baseUrl}/${storeId}/cyd/variants-table`;
        } else {
          return `${baseUrl}/${storeId}/top-products-by-variant`;
        }
      default:
        return null;
    }
  };

  // Hook para obtener datos específicos según la pestaña activa
  const { data: specificData, isLoading: specificLoading } = useQuery({
    queryKey: ['specific-data', config.title, activeTab, storeId, startDate, endDate, minUnitsSold],
    queryFn: async () => {
      const endpoint = getSpecificEndpoint(activeTab);
      if (!endpoint) return null;
      
      const url = new URL(endpoint, process.env.REACT_APP_API_URL || '');
      if (activeTab !== 'historical' && startDate && endDate) {
        url.searchParams.set('startDate', startDate);
        url.searchParams.set('endDate', endDate);
      }
      if (isTopCydProducts && (activeTab === 'products' || activeTab === 'variants')) {
        url.searchParams.set('minUnitsSold', String(minUnitsSold));
      }
      
      const response = await apiService.get(url.pathname + url.search);
      return response.data;
    },
    enabled:
      !!storeId &&
      (activeTab === 'historical' ? true : !!startDate && !!endDate) &&
      [
        'customers',
        'new-customers',
        'recurring-customers',
        'variants',
        'products',
        'period',
        'historical',
        'new',
        'recurring',
      ].includes(activeTab) &&
      (activeTab !== 'products' || isTopCydProducts),
    staleTime: 5 * 60 * 1000,
  });

  const isCydProductsSearch = isTopCydProducts && activeTab === 'products';
  const isCydVariantsSearch = isTopCydProducts && activeTab === 'variants';
  const { data: searchData } = useQuery({
    queryKey: ['cyd-products-search', storeId, startDate, endDate, debouncedSearch, activeTab, minUnitsSold],
    queryFn: async () => {
      const endpoint = isCydVariantsSearch
        ? `/api/metrics/${storeId}/cyd/variants-search`
        : `/api/metrics/${storeId}/cyd/products-search`;
      const url = new URL(endpoint, process.env.REACT_APP_API_URL || '');
      if (startDate && endDate) {
        url.searchParams.set('startDate', startDate);
        url.searchParams.set('endDate', endDate);
      }
      url.searchParams.set('query', debouncedSearch);
      url.searchParams.set('minUnitsSold', String(minUnitsSold));

      const response = await apiService.get(url.pathname + url.search);
      return response.data;
    },
    enabled: (isCydProductsSearch || isCydVariantsSearch) && !!debouncedSearch && !!storeId && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000,
  });

  // Usar datos específicos si están disponibles, sino usar los datos generales
  const getTableData = () => {
    // Si tenemos datos específicos para la pestaña activa, usarlos
    if (
      specificData &&
      ([
        'customers',
        'new-customers',
        'recurring-customers',
        'variants',
        'period',
        'historical',
        'new',
        'recurring',
      ].includes(activeTab) ||
        (isTopCydProducts && (activeTab === 'products' || activeTab === 'variants')))
    ) {
      return Array.isArray(specificData) ? specificData : [];
    }
    
    // Sino, usar los datos generales
    if (!data) return [];
    
    if (Array.isArray(data)) return data;
    if (data.data) return data.data;
    if (data.items) return data.items;
    if (data.products) return data.products;
    if (data.orders) return data.orders;
    
    return [];
  };



  // Usar los datos pasados como props
  const tableData = getTableData();

  // Debug: Ver qué datos llegan
  console.log('🔍 TabbedTableRenderer - Pestaña activa:', activeTab);
  console.log('🔍 TabbedTableRenderer - Datos recibidos:', tableData);
  if (tableData && tableData.length > 0) {
    console.log('📋 Muestra de datos:', tableData[0]);
    console.log('🔍 variant_name en el primer registro:', tableData[0].variant_name);
  }

  // Determinar la pestaña activa por defecto basada en el tipo de datos
  const getDefaultTab = React.useCallback(() => {
    // Si la configuración tiene pestañas personalizadas, usar la primera
    if (config.tabs && config.tabs.length > 0) {
      return config.tabs[0].id;
    }
    
    // Lógica original para compatibilidad
    if (tableData && tableData.length > 0 && tableData[0].customer_email !== undefined) {
      return 'customers';
    }
    return 'products';
  }, [config.tabs, tableData]);

  // Actualizar la pestaña activa solo la primera vez que se cargan los datos
  React.useEffect(() => {
    if (!initialTabSet && tableData && tableData.length > 0) {
      const defaultTab = getDefaultTab();
      setActiveTab(defaultTab);
      setInitialTabSet(true);
    }
  }, [tableData, initialTabSet, getDefaultTab]);

  // Resetear el estado cuando cambia la pestaña activa para reprocesar datos
  React.useEffect(() => {
    setCurrentPage(1);
    setSearchTerm('');
  }, [activeTab]);

  // Resetear el estado cuando cambian los datos para nuevas cargas
  React.useEffect(() => {
    if (tableData && tableData.length === 0) {
      setInitialTabSet(false);
    }
  }, [tableData]);

  // Procesar datos para productos, variantes y clientes
  const processedData = useMemo(() => {
    if (!tableData || tableData.length === 0) return { products: [], variants: [], customers: [] };

    if (config.id === 'top-products' && activeTab === 'products') {
      return { products: tableData, variants: [], customers: [] };
    }

    if (config.id === 'top-products' && activeTab === 'variants') {
      return { products: [], variants: tableData, customers: [] };
    }

    if (config.id === 'top-cyd-products' && activeTab === 'products') {
      return { products: sortCydTableRowsByRateDesc(tableData), variants: [], customers: [] };
    }

    if (config.id === 'top-cyd-products' && activeTab === 'variants') {
      return { products: [], variants: sortCydTableRowsByRateDesc(tableData), customers: [] };
    }

    // Determinar el tipo de datos basado en la estructura
    const hasCustomerEmail = tableData.some((row: any) => row.customer_email !== undefined);
    const hasBorisQuantity = tableData.some((row: any) => row.boris_quantity !== undefined);
    
    console.log('🔍 TabbedTableRenderer - Tipo de datos detectado:', { hasCustomerEmail, hasBorisQuantity });
    console.log('🔍 TabbedTableRenderer - Muestra de datos:', tableData.slice(0, 2));
    
    if (hasCustomerEmail) {
      // Si estamos usando datos específicos, no filtrar
             if (specificData && ['customers', 'new-customers', 'recurring-customers', 'period', 'historical', 'new', 'recurring'].includes(activeTab)) {
         console.log('🔍 TabbedTableRenderer - Usando datos específicos para:', activeTab);
         const customers = tableData.map((row: any) => ({
           customer_email: row.customer_email,
           customer_name: row.customer_name || 'Sin nombre',
           total_amount: row.total_amount || 0,
           total_orders: row.total_orders || 1,
           avg_order_value: row.avg_order_value || 0,
           total_products_sold: row.total_products_sold || 0,
           total_products_cyd: row.total_products_cyd || 0,
           cyd_rate: row.cyd_rate || 0
         }));
        
        console.log('🔍 TabbedTableRenderer - Clientes procesados (datos específicos):', customers);
        return { products: [], variants: [], customers };
      }
      
      // Procesar datos de clientes según la pestaña activa (lógica original para datos generales)
      const customerMap = new Map();

      tableData.forEach((row: any) => {
        const email = row.customer_email;
        const name = row.customer_name || 'Sin nombre';
        const amount = row.total_amount || row.total_line_amount || 0;
        const orders = row.total_orders || 1;
        const avgOrder = row.avg_order_value || 0;
        const cydRate = row.cyd_rate || 0;
        const isNewCustomer = row.is_new_customer || false;
        const isRecurringCustomer = row.is_recurring_customer || false;
        
        console.log('🔍 Procesando cliente:', { email, name, amount, orders, avgOrder, cydRate, isNewCustomer, isRecurringCustomer });
        
        // Filtrar según la pestaña activa (solo para datos generales)
        if (activeTab === 'new-customers' && !isNewCustomer) {
          console.log('🔍 Cliente filtrado (no es nuevo):', email);
          return;
        }
        if (activeTab === 'recurring-customers' && !isRecurringCustomer) {
          console.log('🔍 Cliente filtrado (no es recurrente):', email);
          return;
        }
        
        console.log('🔍 Cliente incluido:', email);
        
        if (customerMap.has(email)) {
          const existing = customerMap.get(email);
          existing.total_amount += amount;
          existing.total_orders += orders;
          existing.avg_order_value = (existing.total_amount / existing.total_orders);
          existing.cyd_rate = Math.max(existing.cyd_rate, cydRate);
        } else {
          customerMap.set(email, {
            customer_email: email,
            customer_name: name,
            total_amount: amount,
            total_orders: orders,
            avg_order_value: avgOrder,
            cyd_rate: cydRate
          });
        }
      });

      const customers = Array.from(customerMap.values())
        .sort((a, b) => b.total_amount - a.total_amount)
        .slice(0, 10);

      console.log('🔍 TabbedTableRenderer - Clientes procesados:', customers);
      return { products: [], variants: [], customers };
    } else {
      // Procesar datos de productos/variantes
      const productMap = new Map();
      const variantMap = new Map();

      tableData.forEach((row: any) => {
        const productKey = row.product_name || 'Sin nombre';
        // Usar variant_name si está disponible, sino usar product_sku
        const variantName = row.variant_name && row.variant_name !== '' ? row.variant_name : row.product_sku;
        const variantKey = `${row.product_name || 'Sin nombre'} - ${variantName || 'Sin SKU'}`;
        
        // Determinar si son datos de CyD o ventas
        const isCydData = row.boris_quantity !== undefined;
        const cydQuantity = isCydData ? (parseInt(row.boris_quantity) || 0) : 0;
        const soldQuantity = parseInt(row.quantity_sold) || 0;

        // Agregar a productos
        if (productMap.has(productKey)) {
          const existing = productMap.get(productKey);
          existing.totalCyd += cydQuantity;
          existing.totalSold += soldQuantity;
          existing.totalAmount += parseFloat(row.total_line_amount) || 0;
          existing.count++;
        } else {
          productMap.set(productKey, {
            product_name: productKey,
            product_category: row.product_category || 'Sin categoría',
            totalCyd: cydQuantity,
            totalSold: soldQuantity,
            totalAmount: parseFloat(row.total_line_amount) || 0,
            count: 1,
            cydRate: 0
          });
        }

        // Agregar a variantes
        if (variantMap.has(variantKey)) {
          const existing = variantMap.get(variantKey);
          existing.totalCyd += cydQuantity;
          existing.totalSold += soldQuantity;
          existing.totalAmount += parseFloat(row.total_line_amount) || 0;
          existing.count++;
        } else {
          variantMap.set(variantKey, {
            product_name: row.product_name || 'Sin nombre',
            product_sku: row.product_sku || 'Sin SKU',
            variant_name: row.variant_name || '',
            product_category: row.product_category || 'Sin categoría',
            totalCyd: cydQuantity,
            totalSold: soldQuantity,
            totalAmount: parseFloat(row.total_line_amount) || 0,
            count: 1,
            cydRate: 0
          });
        }
      });

      // Calcular tasas de CyD y ordenar
      const products = Array.from(productMap.values())
        .map(p => ({ ...p, cydRate: p.totalSold > 0 ? (p.totalCyd / p.totalSold) * 100 : 0 }))
        .sort((a, b) => {
          // Para datos de CyD, ordenar por totalCyd; para ventas, por totalSold
          const isCydData = tableData && tableData.length > 0 && tableData[0].boris_quantity !== undefined;
          return isCydData ? (b.totalCyd - a.totalCyd) : (b.totalSold - a.totalSold);
        })
        .slice(0, 10);

      const variants = Array.from(variantMap.values())
        .map(v => ({ ...v, cydRate: v.totalSold > 0 ? (v.totalCyd / v.totalSold) * 100 : 0 }))
        .sort((a, b) => {
          // Para datos de CyD, ordenar por totalCyd; para ventas, por totalSold
          const isCydData = tableData && tableData.length > 0 && tableData[0].boris_quantity !== undefined;
          return isCydData ? (b.totalCyd - a.totalCyd) : (b.totalSold - a.totalSold);
        })
        .slice(0, 10);

      console.log('📊 Datos procesados - Productos:', products);
      console.log('📊 Datos procesados - Variantes:', variants);
      
      return { products, variants, customers: [] };
    }
  }, [tableData, activeTab, specificData, config.id]);

  // Filtrar datos según la pestaña activa
  const currentData = activeTab === 'products' ? processedData.products : 
                     activeTab === 'variants' ? processedData.variants : 
                     activeTab === 'customers' ? processedData.customers : 
                     activeTab === 'new-customers' ? processedData.customers : 
                     activeTab === 'recurring-customers' ? processedData.customers : 
                     activeTab === 'period' ? processedData.customers : 
                     activeTab === 'historical' ? processedData.customers : 
                     activeTab === 'new' ? processedData.customers : 
                     activeTab === 'recurring' ? processedData.customers : 
                     processedData.products;
  
  console.log('🔍 TabbedTableRenderer - Pestaña activa:', activeTab);
  console.log('🔍 TabbedTableRenderer - Datos actuales:', currentData);
  
  const isServerSearchActive = (isCydProductsSearch || isCydVariantsSearch) && debouncedSearch.length > 0;
  const isTopCydProductsTable = isTopCydProducts;
  const baseData = isServerSearchActive
    ? sortCydTableRowsByRateDesc(Array.isArray(searchData) ? searchData : [])
    : currentData;

  // Filtrar por término de búsqueda
  const filteredData = isServerSearchActive
    ? baseData
    : baseData.filter((item: any) => {
        if (!searchTerm) return true;
        return Object.values(item).some(value => 
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        );
      });

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredData.slice(startIndex, endIndex);
  const cydRateRange = useMemo(
    () => (isTopCydProductsTable ? getCydRateRange(filteredData) : null),
    [filteredData, isTopCydProductsTable]
  );

  const isTableLoading = loading || (isTopCydProducts && specificLoading);

  // Mapeo de nombres de columnas
  const columnNameMapping: Record<string, string> = {
    'product_name': 'Producto',
    'product_sku': 'SKU',
    'variant_name': 'Variante',
    'product_category': 'Categoría',
    'quantity_sold': 'Uds. Vendidas',
    'total_line_amount': 'Total($)',
    'total_sold': 'Uds. vendidas',
    'total_cyd': 'Uds. CyD',
    'totalCyd': 'Uds. CyD',
    'totalSold': 'Uds. vendidas',
    'totalAmount': 'Total ($)',
    'cydRate': 'Tasa CyD (%)',
    'count': 'Órdenes',
    'customer_name': 'Nombre del Cliente',
    'total_amount': 'Total ($)',
    'total_orders': 'Total Órdenes',
    'avg_order_value': 'Promedio Orden',
    'cyd_rate': 'Tasa CyD (%)',
    'total_products_sold': 'Total Productos',
    'total_products_cyd': 'Productos con CyD'
  };

  const formatCellValue = (value: any, columnName: string) => {
    if (value === null || value === undefined) return '-';
    const isTopProducts = config.id === 'top-products' || config.id === 'top-cyd-products';
    
    if (isCydRateColumn(columnName)) {
      const numValue = parseFloat(value);
      const label = isNaN(numValue) ? '0%' : `${numValue.toFixed(2)}%`;

      if (isTopCydProductsTable && cydRateRange) {
        return (
          <BmBadge
            variant="neutral"
            style={getCydRateGrayBadgeStyle(numValue, cydRateRange.min, cydRateRange.max)}
          >
            {label}
          </BmBadge>
        );
      }

      return <BmBadge variant="cyd">{label}</BmBadge>;
    }

    if (isTopProducts && columnName === 'totalAmount') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return '$0';
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        maximumFractionDigits: 0
      }).format(numValue);
    }

    if (isTopProducts && columnName === 'total_line_amount') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return '$0';
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        maximumFractionDigits: 0
      }).format(numValue);
    }
    
    // Formatear como moneda
    if (columnName === 'totalAmount' || columnName === 'total_amount' || columnName === 'avg_order_value') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return '$0';
      return new Intl.NumberFormat('es-AR', { 
        style: 'currency', 
        currency: 'ARS' 
      }).format(numValue);
    }
    
    // Formatear como número
    if (columnName.includes('total') || columnName === 'count' || columnName === 'total_orders' || columnName === 'quantity_sold') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return value.toString();
      return new Intl.NumberFormat('es-AR').format(numValue);
    }
    
    return value.toString();
  };

  const getHeaders = () => {
    // Determinar el tipo de datos
    const hasCustomerEmail = tableData && tableData.length > 0 && tableData[0].customer_email !== undefined;
    const isCydData = tableData && tableData.length > 0 && tableData[0].boris_quantity !== undefined;
    const hideCategory = config.id === 'top-products' || config.id === 'top-cyd-products';
    const isCydProductsTable = config.id === 'top-cyd-products';
    
    const reorderCydColumns = (headers: string[]) => {
      const soldIndex = headers.findIndex(header => header === 'total_sold' || header === 'totalSold');
      const cydIndex = headers.findIndex(header => header === 'total_cyd' || header === 'totalCyd');
      if (soldIndex === -1 || cydIndex === -1 || cydIndex < soldIndex) {
        return headers;
      }
      const reordered = headers.filter((_, index) => index !== soldIndex && index !== cydIndex);
      reordered.splice(cydIndex, 0, headers[cydIndex]);
      reordered.splice(cydIndex + 1, 0, headers[soldIndex]);
      return reordered;
    };

    if (hasCustomerEmail) {
      // Headers para clientes
      return ['customer_name', 'total_amount', 'total_orders', 'avg_order_value', 'total_products_sold', 'total_products_cyd', 'cyd_rate'];
    }

    if (activeTab === 'products') {
      if (isCydProductsTable) {
        return ['product_name', 'total_sold', 'total_cyd', 'cyd_rate'];
      }
      if (config.id === 'top-products') {
        return ['product_name', 'total_orders', 'quantity_sold', 'total_line_amount'];
      }
      if (isCydData) {
        const headers = hideCategory
          ? ['product_name', 'totalCyd', 'totalSold', 'cydRate']
          : ['product_name', 'product_category', 'totalCyd', 'totalSold', 'cydRate'];
        return reorderCydColumns(headers);
      }
      return hideCategory
        ? ['product_name', 'totalSold', 'totalAmount']
        : ['product_name', 'product_category', 'totalSold', 'totalAmount'];
    }

    if (isCydProductsTable) {
      return ['product_name', 'variant_name', 'product_sku', 'total_sold', 'total_cyd', 'cyd_rate'];
    }
    if (config.id === 'top-products') {
      return ['product_name', 'variant_name', 'product_sku', 'total_orders', 'quantity_sold', 'total_line_amount'];
    }
    if (isCydData) {
      const headers = hideCategory
        ? ['product_name', 'variant_name', 'product_sku', 'totalCyd', 'totalSold', 'cydRate']
        : ['product_name', 'variant_name', 'product_sku', 'product_category', 'totalCyd', 'totalSold', 'cydRate'];
      return reorderCydColumns(headers);
    }
    return hideCategory
      ? ['product_name', 'variant_name', 'product_sku', 'totalSold', 'totalAmount']
      : ['product_name', 'variant_name', 'product_sku', 'product_category', 'totalSold', 'totalAmount'];
  };

  const headers = getHeaders();

  const getHeaderLabel = (header: string) => {
    if (config.id === 'top-products') {
      if (header === 'product_name') return 'Producto';
      if (header === 'total_orders') return 'Total Órdenes';
      if (header === 'quantity_sold') return 'Uds. Vendidas';
      if (header === 'total_line_amount') return 'Total($)';
      if (header === 'variant_name') return 'Variante';
      if (header === 'product_sku') return 'SKU';
    }
    if (config.id === 'top-cyd-products') {
      if (header === 'product_name') return 'Producto';
      if (header === 'total_sold') return 'Uds. vendidas';
      if (header === 'total_cyd') return 'Uds. CyD';
      if (header === 'cyd_rate') return 'Tasa CyD (%)';
    }
    return columnNameMapping[header] || header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getCompactHeaderLabel = (header: string) =>
    formatTableHeaderLabel(header, getHeaderLabel(header));

  const tabOptions = useMemo((): PillTabOption[] => {
    if (config.tabs && config.tabs.length > 0) {
      return config.tabs.map((tab) => ({ value: tab.id, label: tab.label }));
    }
    if (tableData && tableData.length > 0 && tableData[0].customer_email !== undefined) {
      return [
        { value: 'customers', label: 'Total Clientes' },
        { value: 'new-customers', label: 'Nuevos Clientes' },
        { value: 'recurring-customers', label: 'Clientes Recurrentes' },
      ];
    }
    return [
      { value: 'products', label: 'Productos' },
      { value: 'variants', label: 'Variantes' },
    ];
  }, [config.tabs, tableData]);

  const showMinUnitsFilter =
    isTopCydProducts && (activeTab === 'products' || activeTab === 'variants');
  const entityLabel = TAB_ENTITY_LABELS[activeTab] || 'registros';
  const searchPlaceholder = TAB_SEARCH_PLACEHOLDERS[activeTab] || 'Buscar en la tabla...';

  if (isTableLoading) {
    return (
      <Card className="metric-panel" shadow="sm" padding="md" radius="md" withBorder>
        <MetricTitle title={config.title} metricId={config.id} mb="xs" />
        <div className="table-loading">
          <div className="loading-spinner"></div>
          <span>Cargando tabla...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="metric-panel" shadow="sm" padding="md" radius="md" withBorder>
        <MetricTitle title={config.title} metricId={config.id} mb="xs" />
        <Text size="sm" c="negative">Error: {error}</Text>
      </Card>
    );
  }

  return (
    <Card className="metric-panel" shadow="sm" padding="md" radius="md" withBorder>
      <MetricTitle title={config.title} metricId={config.id} mb="xs" />

      <MetricTableToolbar
        tabs={tabOptions}
        activeTab={activeTab}
        onTabChange={(value) => {
          setActiveTab(value);
          setCurrentPage(1);
          setSearchTerm('');
        }}
        tabAriaLabel="Vista de tabla"
        showMin={showMinUnitsFilter}
        minValue={minUnitsSold}
        onMinChange={setMinUnitsSold}
        minLabel="Mín. uds. vendidas"
        showSearch
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={searchPlaceholder}
      />

      <div className="table-content">
        {filteredData.length === 0 ? (
          <div className="table-no-data">
            <p>No hay datos disponibles</p>
          </div>
        ) : (
          <>
            <Table className="metric-table">
              <Table.Thead>
                <Table.Tr>
                  {headers.map((header) => (
                    <Table.Th key={header} className={getTableColumnClass(header)}>
                      {getCompactHeaderLabel(header)}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {paginatedData.map((row: any, index: number) => (
                  <Table.Tr key={index}>
                    {headers.map((header) => (
                      <Table.Td key={header} className={getTableColumnClass(header)}>
                        {formatCellValue(row[header], header)}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination total={totalPages} value={currentPage} onChange={setCurrentPage} />
              </Group>
            )}

            <Text size="xs" c="dimmed" ta="center" mt="xs">
              Mostrando {startIndex + 1}-{Math.min(endIndex, filteredData.length)} de{' '}
              {filteredData.length} {entityLabel}
            </Text>
          </>
        )}
      </div>
    </Card>
  );
};

export default TabbedTableRenderer;
